import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Share } from "lucide-react";
import { enablePush, disablePush, pushSupported, pushPermission } from "../lib/push";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const NotificationsCard = () => {
  const [state, setState] = useState("idle"); // idle | on | blocked | ios-install | unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isIOS() && !isStandalone()) return setState("ios-install");
    if (!pushSupported()) return setState("unsupported");
    const perm = pushPermission();
    if (perm === "denied") setState("blocked");
    else if (perm === "granted" && localStorage.getItem("mc_fcm_token")) setState("on");
    else setState("idle");
  }, []);

  const turnOn = async () => {
    setBusy(true);
    try { await enablePush(); setState("on"); toast.success("Notifications enabled"); }
    catch (e) { toast.error(e.message || "Couldn't enable notifications"); if (pushPermission() === "denied") setState("blocked"); }
    finally { setBusy(false); }
  };
  const turnOff = async () => {
    setBusy(true);
    try { await disablePush(); setState("idle"); toast.success("Notifications turned off"); }
    finally { setBusy(false); }
  };

  const iconOn = state === "on";
  const subtitle = {
    on: "You'll get new dispatches & urgent prayers.",
    blocked: "Blocked in your browser settings — enable there to turn on.",
    "ios-install": "On iPhone, tap Share ⬆️ then \u201CAdd to Home Screen\u201D, open the app, then enable here.",
    unsupported: "Your current browser doesn't support push. Try Chrome, or install the app.",
    idle: "Get notified of new dispatches & urgent prayers.",
  }[state];

  return (
    <div className="bg-white rounded-2xl p-4 shadow-[0_4px_24px_rgba(44,44,62,0.04)] mb-5 flex items-center gap-3" data-testid="notifications-card">
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${iconOn ? "accent-bg text-white" : "bg-[var(--brand-navy)]/8 text-[var(--brand-navy)]"}`}>
        {state === "ios-install" ? <Share className="h-5 w-5" /> : iconOn ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[#2C2C3E]">Push notifications</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
      {state === "on" ? (
        <button data-testid="notifications-off-btn" onClick={turnOff} disabled={busy} className="shrink-0 text-sm rounded-full border px-4 py-2 hover:bg-muted/40 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Turn off"}
        </button>
      ) : state === "idle" ? (
        <button data-testid="notifications-on-btn" onClick={turnOn} disabled={busy} className="shrink-0 text-sm rounded-full accent-bg text-white px-4 py-2 hover:opacity-90 transition-opacity disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enable"}
        </button>
      ) : null}
    </div>
  );
};

export default NotificationsCard;
